import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { EmpresaUser, Empresa } from '@/types';
import { portalApi, clearPortalApiAuthToken, setPortalApiAuthToken } from '@/services/portal-api';

const CACHE_DURATION_MS = 10 * 60 * 60 * 1000; // 10 horas

let hasHydratedFlag = false;
const hydrationCallbacks: (() => void)[] = [];

export function onPortalAuthHydrated(callback: () => void) {
  if (hasHydratedFlag) {
    callback();
    return () => {};
  }
  hydrationCallbacks.push(callback);
  return () => {
    const idx = hydrationCallbacks.indexOf(callback);
    if (idx !== -1) hydrationCallbacks.splice(idx, 1);
  };
}

interface PortalAuthState {
  user: EmpresaUser | null;
  empresa: Empresa | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  expiresAt: number | null;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearError: () => void;
}

interface LoginResponse {
  user: EmpresaUser;
  accessToken: string;
}

export const usePortalAuthStore = create<PortalAuthState>()(
  persist(
    (set, get) => ({
      user: null,
      empresa: null,
      accessToken: null,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      error: null,
      expiresAt: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await portalApi.post<{ success: boolean; data: LoginResponse }>('/portal/login', {
            email,
            password,
          });

          const { user, accessToken } = response.data;
          setPortalApiAuthToken(accessToken);

          const expiresAt = Date.now() + CACHE_DURATION_MS;

          set({
            user,
            accessToken,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
            error: null,
            expiresAt,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro ao fazer login';
          clearPortalApiAuthToken();
          set({
            user: null,
            empresa: null,
            accessToken: null,
            isLoading: false,
            isAuthenticated: false,
            error: errorMessage,
            expiresAt: null,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await portalApi.post('/portal/logout');
        } catch {
          // Ignore logout network errors and always clear local auth state.
        }

        clearPortalApiAuthToken();
        set({
          user: null,
          empresa: null,
          accessToken: null,
          isAuthenticated: false,
          isInitialized: true,
          error: null,
          expiresAt: null,
        });
      },

      initializeAuth: async () => {
        if (get().isInitialized) {
          return;
        }

        set({ isLoading: true, error: null });

        let { accessToken, expiresAt } = get();

        if (!accessToken) {
          try {
            const raw = localStorage.getItem('portal-auth-storage');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.state?.accessToken) {
                accessToken = parsed.state.accessToken as string;
                expiresAt = parsed.state.expiresAt as number | null;
              }
            }
          } catch {
            // ignore parse errors
          }
        }

        if (accessToken && expiresAt && Date.now() < expiresAt) {
          setPortalApiAuthToken(accessToken);
          set({
            accessToken,
            expiresAt,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
          });

          try {
            const response = await portalApi.post<{ success: boolean; data: LoginResponse }>('/portal/refresh', undefined, { skipAuthRefresh: true });
            const { user, accessToken: newAccessToken } = response.data;

            setPortalApiAuthToken(newAccessToken);
            set({
              user,
              accessToken: newAccessToken,
              expiresAt: Date.now() + CACHE_DURATION_MS,
            });
          } catch {
            // Ignora erro de refresh em background
          }
          return;
        }

        try {
          const response = await portalApi.post<{ success: boolean; data: LoginResponse }>('/portal/refresh', undefined, { skipAuthRefresh: true });
          const { user, accessToken: newAccessToken } = response.data;

          setPortalApiAuthToken(newAccessToken);
          set({
            user,
            accessToken: newAccessToken,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
            error: null,
            expiresAt: Date.now() + CACHE_DURATION_MS,
          });
        } catch {
          clearPortalApiAuthToken();
          set({
            user: null,
            empresa: null,
            accessToken: null,
            isAuthenticated: false,
            isInitialized: true,
            isLoading: false,
            expiresAt: null,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'portal-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        expiresAt: state.expiresAt,
      }),
      onRehydrateStorage: () => (state) => {
        hasHydratedFlag = true;
        hydrationCallbacks.forEach((cb) => cb());

        if (state?.accessToken) {
          if (state.expiresAt && Date.now() > state.expiresAt) {
            clearPortalApiAuthToken();
          } else {
            setPortalApiAuthToken(state.accessToken);
          }
        }
      },
    }
  )
);
